import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { STORAGE_KEYS, USER_ROLES, UserRole, DISABLE_SUBSCRIPTION } from '../helpers/constants';
import { RegisterResponse, LoginResponse } from '../services/authService';
import { getMySubscription } from '../services/companyService';
import { setMySubscriptionData, type MySubscriptionStoredData } from '../helpers/storageUtils';

interface User {
  role: UserRole;
  email?: string;
  /** Login handle or legacy single field from API */
  name?: string;
  /** Human-readable display name when API/JWT provides it separately from username */
  full_name?: string;
  id?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (token: string, userData?: User) => void;
  loginWithResponse: (response: LoginResponse) => void;
  register: (response: RegisterResponse) => void;
  logout: () => void;
  /** When true, app should redirect to centralized sessionExpired page (401) */
  sessionExpired: boolean;
  /** Call when any API returns 401 to show centralized "Session Expired" page */
  markSessionExpired: () => void;
  token: string | null;
  isAdmin: boolean;
  isUser: boolean;
  isSupport: boolean;
  isInitializing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During hot reload or development, this can happen temporarily
    // Return a safe default instead of throwing to prevent crashes
    if (process.env.NODE_ENV === 'development') {
      console.warn('useAuth called outside AuthProvider - returning default values');
      return {
        isAuthenticated: false,
        user: null,
        login: () => {},
        loginWithResponse: () => {},
        register: () => {},
        logout: () => {},
        sessionExpired: false,
        markSessionExpired: () => {},
        token: null,
        isAdmin: false,
        isUser: false,
        isSupport: false,
        isInitializing: true,
      };
    }
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN));
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  /** When true, redirect to /sessionExpired for centralized 401 handling */
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  // Ref mirror of isAuthenticated — updated synchronously in logout/login so that
  // async callbacks (e.g. Sidebar interval 401s) always read the current value
  // even before React has had a chance to flush the next render cycle.
  const isAuthenticatedRef = useRef<boolean>(!!token);

  // Parse user data from token if available
  useEffect(() => {
    setIsInitializing(true);
    
    if (token) {
      // Skip token parsing for mock tokens
      if (token.startsWith('mock.')) {
        // Don't try to parse mock tokens, just keep the current state
        setIsInitializing(false);
        return;
      }

      try {
        // Check if token is a valid JWT format (has at least 2 dots)
        if (token.split('.').length >= 3) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userData = {
            role: (payload.role as UserRole) || USER_ROLES.USER,
            email: payload.email ?? payload.email_id,
            name: payload.name,
            full_name: payload.full_name ?? payload.fullName,
            id: payload.id ?? payload.user_id,
          };
            setUser(userData);
            setIsAuthenticated(true);
            localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
            setIsInitializing(false);
        } else {
          // Token is not JWT format, but we might have user data stored
          const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
          if (storedUserData) {
            try {
              const parsedData = JSON.parse(storedUserData);
              const userData = {
                role: (parsedData.role as UserRole) || USER_ROLES.USER,
                email: parsedData.email_id,
                name: parsedData.name,
                full_name: parsedData.full_name ?? parsedData.fullName,
                id: parsedData.user_id,
              };
              setUser(userData);
              setIsAuthenticated(true);
              setIsInitializing(false);
            } catch (parseError) {
              console.error('Error parsing stored user data:', parseError);
              setIsInitializing(false);
              logout();
            }
          } else {
            setIsInitializing(false);
            logout();
          }
        }
      } catch (error) {
        console.error('Error parsing JWT token:', error);
        // Try to get user data from stored response
        const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (storedUserData) {
          try {
            const parsedData = JSON.parse(storedUserData);
            const userData = {
              role: (parsedData.role as UserRole) || USER_ROLES.USER,
              email: parsedData.email_id,
              name: parsedData.name,
              full_name: parsedData.full_name ?? parsedData.fullName,
              id: parsedData.user_id,
            };
            setUser(userData);
            setIsAuthenticated(true);
            setIsInitializing(false);
          } catch (parseError) {
            console.error('Error parsing stored user data after JWT parse error:', parseError);
            setIsInitializing(false);
            logout();
          }
        } else {
          setIsInitializing(false);
          logout();
        }
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setIsInitializing(false);
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    }
  }, [token]);

  const login = (newToken: string, userData?: User) => {
    isAuthenticatedRef.current = true;
    setToken(newToken);
    setIsAuthenticated(true);
    setIsInitializing(false);
    setSessionExpired(false);
    if (userData) {
      setUser(userData);
    }
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
  };

  const loginWithResponse = (response: LoginResponse) => {
    isAuthenticatedRef.current = true;
    setSessionExpired(false);
    // Set user data directly from response (don't rely on JWT parsing)
    const userData = {
      role: (response.role as UserRole) || USER_ROLES.USER,
      email: response.email_id,
      name: response.name,
      full_name: response.full_name,
      id: response.user_id,
    };
    
    setUser(userData);
    setIsAuthenticated(true);
    setIsInitializing(false);
    setToken(response.access_token);
    
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.access_token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token);
    // Store full response for profile and future use
    try {
      localStorage.setItem(STORAGE_KEYS.LOGGED_IN_USER_DATA, JSON.stringify(response));
    } catch {}
  };

  const register = (response: RegisterResponse) => {
    isAuthenticatedRef.current = true;
    setToken(response.access_token);
    setIsAuthenticated(true);
    setIsInitializing(false);
    setSessionExpired(false);
    const userData = {
      role: (response.role as UserRole) || USER_ROLES.USER,
      email: response.email_id,
      name: response.name,
      full_name: response.full_name,
      id: response.user_id,
    };
    
    setUser(userData);
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.access_token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token);
    // Store full response for profile and future use
    try {
      localStorage.setItem(STORAGE_KEYS.LOGGED_IN_USER_DATA, JSON.stringify(response));
    } catch {}
  };

  const logout = () => {
    isAuthenticatedRef.current = false;
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setIsInitializing(false);
    setSessionExpired(false);
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.MY_SUBSCRIPTION_DATA);
  };

  /** Call when any API returns 401 so app redirects to centralized sessionExpired page */
  const markSessionExpired = useCallback(() => {
    // Read the ref, not closed-over state. The ref is always current even when
    // called from a stale interval closure (e.g. Sidebar's 30-second poll) that
    // was created while isAuthenticated was true but fires after logout().
    if (!isAuthenticatedRef.current) return;
    setSessionExpired(true);
  }, []);

  // For admin users: fetch COMPANY.MY_SUBSCRIPTION when subscription enabled and store in MY_SUBSCRIPTION_DATA
  useEffect(() => {
    if (DISABLE_SUBSCRIPTION || !isAuthenticated || !user || user.role !== USER_ROLES.ADMIN) return;
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) return;
    getMySubscription(refreshToken)
      .then((res) => {
        if (res?.success && res?.data) {
          setMySubscriptionData(res.data as unknown as MySubscriptionStoredData);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, user]);

  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const isUser = user?.role === USER_ROLES.USER;
  const isSupport = user?.role === USER_ROLES.SUPPORT;

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      loginWithResponse,
      register,
      logout, 
      sessionExpired,
      markSessionExpired,
      token, 
      isAdmin, 
      isUser,
      isSupport,
      isInitializing
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 